import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, DollarSign, FileText, TrendingUp } from "lucide-react";

const KIND_ICON = {
  invoice_created: FileText,
  payment_received: DollarSign,
  incentive_credited: TrendingUp,
};

const KIND_TINT = {
  invoice_created: "text-brand-dark bg-brand-tint border-brand/30",
  payment_received: "text-emerald-700 bg-emerald-50 border-emerald-200",
  incentive_credited: "text-violet-700 bg-violet-50 border-violet-200",
};

export default function Notifications() {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const load = async () => { const { data } = await api.get("/api/notifications"); setItems(data); };
  useEffect(() => { load(); }, []);

  const open = async (n) => {
    if (!n.read) {
      try { await api.post(`/api/notifications/${n.id}/read`); } catch { /* ignore */ }
    }
    if (n.link) navigate(n.link);
    else load();
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div>
        <h1 className="text-3xl font-heading font-semibold tracking-tight">Notifications</h1>
        <p className="text-sm text-slate-500 mt-1">Alerts about invoicing, payments, and BD incentives on your projects.</p>
      </div>
      <Card className="border-slate-200 shadow-none p-4">
        {items.length === 0 && (
          <div className="text-center text-slate-500 py-12">
            <Bell className="w-8 h-8 mx-auto mb-3 text-slate-400" />
            No notifications yet.
          </div>
        )}
        {items.map((n) => {
          const Icon = KIND_ICON[n.kind] || Bell;
          const tint = KIND_TINT[n.kind] || "text-slate-600 bg-slate-50 border-slate-200";
          return (
            <div
              key={n.id}
              className={`border-b border-slate-100 py-3 flex items-start gap-3 ${!n.read ? "bg-brand-tint/30" : ""} ${n.link ? "cursor-pointer hover:bg-slate-50" : ""} rounded-md px-2 -mx-2 transition`}
              onClick={() => open(n)}
              data-testid={`notif-${n.id}`}
            >
              <div className={`w-8 h-8 rounded-full inline-flex items-center justify-center border ${tint} shrink-0`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium flex items-center gap-2">
                  {n.title}
                  {!n.read && <span className="w-2 h-2 rounded-full bg-brand"></span>}
                </div>
                <div className="text-sm text-slate-600 mt-0.5">{n.message}</div>
                <div className="text-xs text-slate-400 mt-1">{n.created_at?.replace("T", " ").slice(0, 19)}</div>
              </div>
              {!n.read && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async (e) => { e.stopPropagation(); await api.post(`/api/notifications/${n.id}/read`); load(); }}
                  data-testid={`mark-read-${n.id}`}
                >
                  Mark read
                </Button>
              )}
            </div>
          );
        })}
      </Card>
    </div>
  );
}
